import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { renderEtkp, renderEur, renderInt, renderRate } from './shared';

/**
 * Common row shape consumed by the KPI cards. Both ExternalTab and FtTab
 * history rows shape-compatibly satisfy this — fields we don't have on FT
 * are nullable, and the KPI calc only counts non-null contributors.
 *
 * EVERY numeric field arrives as `number | string | null` because mysql2
 * serializes BIGINT (COUNT) + DECIMAL (SUM, money) as strings — `a += b`
 * would concatenate. We coerce via `toFloat` before any arithmetic. The
 * 2026-06-09 SOI-as-string incident proved this is not optional even for
 * fields the TS types claim are `number`.
 */
export interface HistoryKpiRow {
  recipients: number | string | null;
  unique_opens: number | string | null;
  unique_clicks: number | string | null;
  unsubscribed: number | string | null;
  sois: number | string | null;
  leads: number | string | null;
  revenue: number | string | null;
}

interface Props {
  rows: HistoryKpiRow[];
  /** Optional label shown next to the row count. */
  scopeLabel?: string;
}

const toFloat = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

const HistoryKpiCards: React.FC<Props> = ({ rows, scopeLabel }) => {
  const k = useMemo(() => {
    let nSends = 0;
    let recipients = 0;
    let opens = 0;
    let clicks = 0;
    let unsubs = 0;
    let sois = 0;
    let leads = 0;
    let revenue = 0;

    // Weighted rates use only rows where BOTH numerator and denominator are
    // present — a NULL opens row contributes nothing to opens but also
    // doesn't penalise the denominator (i.e. it's excluded entirely from the
    // open-rate calc, treated as "no data" not "0 opens"). Same for clicks,
    // unsubs, SOIs, leads, revenue/ETKP. Fresh sends (~24h before VNR
    // attribution lands) and AA rows where backend left attribution NULL
    // otherwise dilute the SOI/lead/ETKP rates downward.
    let recipientsForOpens = 0;
    let recipientsForClicks = 0;
    let recipientsForUnsubs = 0;
    let recipientsForSois = 0;
    let recipientsForLeads = 0;
    let recipientsForRevenue = 0;

    let anySoi = false;
    let anyLeads = false;
    let anyRevenue = false;

    for (const r of rows) {
      nSends++;
      // Coerce EVERY field — mysql2 returns DECIMAL/BIGINT as string.
      const rec = toFloat(r.recipients);
      const op = toFloat(r.unique_opens);
      const cl = toFloat(r.unique_clicks);
      const un = toFloat(r.unsubscribed);
      const so = toFloat(r.sois);
      const ld = toFloat(r.leads);
      const rev = toFloat(r.revenue);

      if (rec != null) recipients += rec;

      if (rec != null && op != null) {
        opens += op;
        recipientsForOpens += rec;
      }
      if (rec != null && cl != null) {
        clicks += cl;
        recipientsForClicks += rec;
      }
      if (rec != null && un != null) {
        unsubs += un;
        recipientsForUnsubs += rec;
      }
      if (so != null) {
        sois += so;
        anySoi = true;
        if (rec != null) recipientsForSois += rec;
      }
      if (ld != null) {
        leads += ld;
        anyLeads = true;
        if (rec != null) recipientsForLeads += rec;
      }
      if (rev != null) {
        revenue += rev;
        anyRevenue = true;
        if (rec != null) recipientsForRevenue += rec;
      }
    }

    return {
      nSends,
      recipients,
      opens,
      clicks,
      unsubs,
      sois: anySoi ? sois : null,
      leads: anyLeads ? leads : null,
      revenue: anyRevenue ? revenue : null,
      recipientsForOpens,
      recipientsForClicks,
      recipientsForUnsubs,
      recipientsForSois,
      recipientsForLeads,
      recipientsForRevenue,
    };
  }, [rows]);

  const cardBase = {
    flex: '1 1 0',
    minWidth: 150,
    backgroundColor: '#ffffff',
  } as const;

  return (
    <Box
      display="flex"
      gap={1.5}
      flexWrap="wrap"
      sx={{ mb: 2 }}
      role="region"
      aria-label="KPIs für gefilterte Sendungen"
    >
      <Card sx={cardBase}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            Sendungen{scopeLabel ? ` · ${scopeLabel}` : ''}
          </Typography>
          <Typography variant="h6">{renderInt(k.nSends)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Empfänger gesamt: {renderInt(k.recipients)}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={cardBase}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            Opens (gewichtet)
          </Typography>
          <Typography variant="h6">{renderRate(k.opens, k.recipientsForOpens)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Σ {renderInt(k.opens)}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={cardBase}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            Clicks (gewichtet)
          </Typography>
          <Typography variant="h6">{renderRate(k.clicks, k.recipientsForClicks)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Σ {renderInt(k.clicks)}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={cardBase}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            SOI gesamt
          </Typography>
          <Typography variant="h6">{renderInt(k.sois)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {renderRate(k.sois, k.recipientsForSois, 2)} v. Empf.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={cardBase}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            Leads gesamt
          </Typography>
          <Typography variant="h6">{renderInt(k.leads)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {renderRate(k.leads, k.recipientsForLeads, 2)} v. Empf.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={cardBase}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            Revenue
          </Typography>
          <Typography variant="h6">{renderEur(k.revenue)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Σ über {renderInt(k.recipientsForRevenue)} Empf.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={cardBase}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary">
            €/1k Empf. (ETKP)
          </Typography>
          <Typography variant="h6">{renderEtkp(k.revenue, k.recipientsForRevenue)}</Typography>
          <Typography variant="caption" color="text.secondary">
            gewichtet über Sendungen mit Revenue
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HistoryKpiCards;
